// ClassesPanel — list, add, and edit classes (e.g. "5A")
// class_teacher_id is a display label only — see the note below the form.
import { useEffect, useState } from 'react';
import { getClasses, createClass, updateClass, getStaff } from '../api/client';
import Button from '../ui/Button';
import { Card, CardHeader } from '../ui/Card';

const EMPTY = { name: '', class_teacher_id: '' };

export default function ClassesPanel() {
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [form,    setForm]    = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [msg,     setMsg]     = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const [c, s] = await Promise.all([getClasses(), getStaff()]);
      setClasses(c);
      setTeachers(s.filter(x => x.designation === 'Teacher'));
    } catch { setMsg('Failed to load classes'); }
  }

  function startEdit(c) {
    setEditing(c.id);
    setForm({ name: c.name, class_teacher_id: c.class_teacher_id || '' });
  }

  function cancel() { setEditing(null); setForm(EMPTY); setMsg(''); }

  function set(key) { return e => setForm({ ...form, [key]: e.target.value }); }

  async function save(e) {
    e.preventDefault();
    try {
      const payload = { name: form.name, class_teacher_id: form.class_teacher_id || null };
      editing ? await updateClass(editing, payload) : await createClass(payload);
      setMsg(editing ? 'Class updated ✅' : 'Class added ✅');
      cancel(); load();
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.error || err.message));
    }
  }

  const inputCls = "border border-line rounded-s px-3 py-2 text-sm bg-paper-raised focus:outline-none focus:ring-2 focus:ring-transit mt-1";
  const labelCls = "flex flex-col text-xs font-bold text-ink-soft";

  return (
    <div>
      <div className="mb-5">
        <h2 className="font-display font-extrabold text-xl text-ink mb-1">Classes</h2>
        <p className="text-sm text-ink-soft">
          "Class teacher" is a <strong>label only</strong> — any teacher can take attendance for
          any class from the staff app, so one teacher being absent never leaves a class with
          nobody able to mark attendance.
        </p>
      </div>

      <Card className="p-5 mb-6 max-w-[440px] flex flex-col gap-3">
        <h3 className="font-display font-bold text-[15px] text-ink m-0">{editing ? 'Edit Class' : 'Add Class'}</h3>

        <form onSubmit={save} className="flex flex-col gap-3">
          <label className={labelCls}>Name
            <input className={inputCls} required value={form.name} onChange={set('name')} placeholder="e.g. 5A" />
          </label>
          <label className={labelCls}>Class Teacher (label only)
            <select className={inputCls} value={form.class_teacher_id} onChange={set('class_teacher_id')}>
              <option value="">— None —</option>
              {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
          <div className="flex gap-2">
            <Button type="submit">{editing ? 'Update' : 'Add Class'}</Button>
            {editing && <Button variant="secondary" type="button" onClick={cancel}>Cancel</Button>}
          </div>
          {msg && (
            <p className={`text-sm font-bold px-3 py-2 rounded-s inline-block w-fit ${msg.includes('Error') ? 'bg-critical-tint text-critical' : 'bg-success-tint text-success'}`}>
              {msg}
            </p>
          )}
        </form>
      </Card>

      <Card>
        <CardHeader title={`Classes (${classes.length})`} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                {['ID','Name','Class Teacher','Actions'].map(h => (
                  <th key={h} className="px-4 py-2.5">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {classes.map(c => (
                <tr key={c.id} className="border-t border-line-soft hover:bg-transit-tint/40">
                  <td className="px-4 py-3 text-ink-soft tabular-nums">{c.id}</td>
                  <td className="px-4 py-3 font-semibold text-ink">{c.name}</td>
                  <td className="px-4 py-3">{c.class_teacher_name || '—'}</td>
                  <td className="px-4 py-3">
                    <Button variant="secondary" className="!px-2.5 !py-1 !text-xs" onClick={() => startEdit(c)}>Edit</Button>
                  </td>
                </tr>
              ))}
              {classes.length === 0 && (
                <tr><td colSpan={4} className="text-center py-8 text-ink-soft">No classes yet — add one above</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
